/**
 * SiteUp: rótulo de "há quanto tempo esta conversa espera".
 *
 * Existia duplicado em OnboardingView.vue e ConversationHeader.vue, e os dois
 * paravam em horas: uma conversa parada há quatro meses aparecia como
 * "espera há 3194 h", um número que ninguém lê como urgência. Aqui a escala
 * continua até dias e semanas, e a decisão de qual unidade usar mora num lugar
 * só.
 */

const MINUTO = 1;
const HORA = 60;
const DIA = 60 * 24;
const SEMANA = DIA * 7;

/**
 * Minutos inteiros de espera a partir de `waiting_since`.
 *
 * A API manda epoch em SEGUNDOS, mas parte do front já carregava conversas com
 * o valor em milissegundos; o corte em 1e12 aceita os dois sem adivinhar.
 * Sem espera registrada devolve 0, não null — quem chama trata "0" como
 * "acabou de esperar", que é a leitura certa.
 */
export const waitingMinutesFromEpoch = since => {
  if (!since) return 0;
  const raw = Number(since);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  const ms = raw < 1e12 ? raw * 1000 : raw;
  return Math.max(0, Math.floor((Date.now() - ms) / 60000));
};

/**
 * @param {number|null} minutes minutos de espera (use waitingMinutesFromEpoch)
 * @param {Function} t o `t` do vue-i18n
 * @returns {string} texto pronto, ou '' quando não há espera registrada
 */
export const waitingLabelFromMinutes = (minutes, t) => {
  if (minutes === null || minutes === undefined) return '';
  if (minutes < MINUTO) return t('CHAT_LIST.FOCUS_QUEUE.WAITING_NOW');
  if (minutes < HORA) return t('CHAT_LIST.FOCUS_QUEUE.WAITING', { n: minutes });
  if (minutes < DIA) {
    return t('CHAT_LIST.FOCUS_QUEUE.WAITING_HOURS', {
      n: Math.floor(minutes / HORA),
    });
  }
  if (minutes < SEMANA) {
    return t('CHAT_LIST.FOCUS_QUEUE.WAITING_DAYS', {
      n: Math.floor(minutes / DIA),
    });
  }
  return t('CHAT_LIST.FOCUS_QUEUE.WAITING_WEEKS', {
    n: Math.floor(minutes / SEMANA),
  });
};

/** Atalho para quem tem o objeto da conversa em mãos. */
export const waitingLabelFor = (chat, t) =>
  waitingLabelFromMinutes(waitingMinutesFromEpoch(chat?.waiting_since), t);

/**
 * Faixa de urgência, para agrupar e colorir a fila.
 * @returns {'now'|'hours'|'days'|null}
 */
export const waitingTierFromMinutes = minutes => {
  if (minutes === null || minutes === undefined) return null;
  if (minutes >= DIA) return 'days';
  if (minutes >= HORA) return 'hours';
  return 'now';
};

/**
 * Só a DURAÇÃO ("133 dias", "3 h", "42 min"), sem o "espera há".
 *
 * O Hub já escreve "Maior espera:" antes do número, então reaproveitar o
 * rótulo completo daria "Maior espera: espera há 133 dias".
 */
export const waitingDurationFromMinutes = (minutes, t) => {
  if (minutes === null || minutes === undefined) return '';
  if (minutes < HORA)
    return t('CHAT_LIST.WAITING_DURATION.MINUTES', { n: minutes });
  if (minutes < DIA) {
    return t('CHAT_LIST.WAITING_DURATION.HOURS', {
      n: Math.floor(minutes / HORA),
    });
  }
  if (minutes < SEMANA) {
    return t('CHAT_LIST.WAITING_DURATION.DAYS', {
      n: Math.floor(minutes / DIA),
    });
  }
  return t('CHAT_LIST.WAITING_DURATION.WEEKS', {
    n: Math.floor(minutes / SEMANA),
  });
};

/** Ordem de exibição das faixas: o que espera mais aparece primeiro. */
const ORDEM_FAIXAS = ['days', 'hours', 'now'];

/**
 * Quebra a fila em grupos por faixa de espera, preservando a ordem que o
 * comparador já deu dentro de cada grupo.
 *
 * Devolve uma lista PLANA de linhas para a lista virtualizada consumir, porque
 * o Virtualizer precisa de um array só: `{ type: 'header' }` para o cabeçalho
 * do grupo e `{ type: 'chat' }` para a conversa. Grupo vazio não gera
 * cabeçalho.
 *
 * @param {Array} conversations conversas já ordenadas por espera
 * @returns {Array<{type:'header',tier:string,count:number,key:string}
 *                |{type:'chat',chat:object,key:string}>}
 */
export const groupQueueByWaiting = conversations => {
  if (!Array.isArray(conversations) || !conversations.length) return [];

  const porFaixa = { days: [], hours: [], now: [] };
  conversations.forEach(chat => {
    const minutos = waitingMinutesFromEpoch(chat?.waiting_since);
    const faixa = waitingTierFromMinutes(minutos) || 'now';
    porFaixa[faixa].push(chat);
  });

  const linhas = [];
  ORDEM_FAIXAS.forEach(faixa => {
    const grupo = porFaixa[faixa];
    if (!grupo.length) return;
    linhas.push({
      type: 'header',
      tier: faixa,
      count: grupo.length,
      key: `header-${faixa}`,
    });
    grupo.forEach(chat => {
      linhas.push({ type: 'chat', chat, key: `chat-${chat.id}` });
    });
  });
  return linhas;
};
